import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User, UserProfile, LoginRequest, RegisterRequest, ChangePasswordRequest } from '../types';
import * as authApi from '../services/authApi';
import { activityTracker } from '../services/activityTracker';
import { message } from 'antd';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (data: ChangePasswordRequest) => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false); // Prevent concurrent loadUser calls

  // Load user on mount if authenticated
  const loadUser = useCallback(async () => {
    // Prevent concurrent calls (especially important for React StrictMode)
    if (loadingRef.current) {
      console.log('[AuthContext] loadUser already in progress, skipping...');
      return;
    }

    loadingRef.current = true;

    try {
      if (authApi.isAuthenticated()) {
        console.log('[AuthContext] Loading user data...');
        const userData = await authApi.getCurrentUser();
        setUser(userData);
        setProfile(userData.profile || null);

        // Start activity tracking when user is authenticated
        activityTracker.start();
      } else {
        console.log('[AuthContext] No authentication token found');
      }
    } catch (error: any) {
      console.error('[AuthContext] Failed to load user:', error);

      // Don't clear tokens on rate limit errors
      if (!error.isRateLimit) {
        authApi.clearTokens();
      }
      activityTracker.stop();
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    loadUser();

    return () => {
      // Cleanup: abort any in-flight requests
      abortController.abort();
    };
  }, [loadUser]);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await authApi.login(credentials);
      setUser(response.user);
      setProfile(response.profile || null);

      // Start activity tracking
      activityTracker.start();

      message.success(`Welcome back, ${response.user.first_name}!`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      message.error(errorMessage);
      throw error;
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      const response = await authApi.register(userData);
      setUser(response.user);
      setProfile(response.profile || null);

      // Start activity tracking
      activityTracker.start();

      message.success('Account created successfully!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Registration failed';
      message.error(errorMessage);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Stop activity tracking
      activityTracker.stop();

      await authApi.logout();
      setUser(null);
      setProfile(null);
      message.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      message.error('Logout failed');
    }
  };

  const changePassword = async (data: ChangePasswordRequest) => {
    try {
      await authApi.changePassword(data);
      message.success('Password changed successfully. Please log in again.');

      // Stop activity tracking
      activityTracker.stop();

      // Password change logs out all sessions
      setUser(null);
      setProfile(null);
      authApi.clearTokens();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Password change failed';
      message.error(errorMessage);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authApi.getCurrentUser();
      setUser(userData);
      setProfile(userData.profile || null);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    profile,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    changePassword,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
